import chevronRight from "@layouts/betto/assets/chevronRight.svg";
import {
	getBettoSliderRowAspectRatio,
	getImageSrc,
} from "@pages/home/betto/casinoGridSlider/gameCard.jsx";
import { preloadBettoImages } from "@pages/home/betto/utils";
import React, { useEffect, useState, useRef } from "react";
import { getTranslate } from "react-localize-redux";
import { useSelector } from "react-redux";
import {
	AllButton,
	ArrowButton,
	ArrowIcon,
	Card,
	Controls,
	HEADER,
	TitleBox,
	Track,
	ViewAllCard,
	ViewAllIcon,
	ViewAllLabel,
	Viewport,
	WRAPPER,
} from "./elements";

const GAP = 10;
const STEP = 4;
const selectTranslate = (state) => getTranslate(state.localize);

const getVisibleCount = (width) => {
	if (width >= 1440) return 8;
	if (width >= 1024) return 7;
	if (width >= 768) return 5;
	return 3;
};

export default function BettoGameSlider({
	icon,
	title,
	items,
	renderItem,
	viewAllLink,
	goToLink,
	keyPrefix,
}) {
	const translate = useSelector(selectTranslate);
	const [visibleCount, setVisibleCount] = useState(() =>
		getVisibleCount(typeof window !== "undefined" ? window.innerWidth : 1440)
	);
	const [index, setIndex] = useState(0);

	const totalItems = (items?.length ?? 0) + 1;
	const maxIndex = Math.max(0, totalItems - visibleCount);

	useEffect(() => {
		const onResize = () => setVisibleCount(getVisibleCount(window.innerWidth));

		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		setIndex((current) => Math.min(current, maxIndex));
	}, [maxIndex]);

	useEffect(() => {
		if (!items?.length) return;

		const preloadCount = Math.min(visibleCount, items.length);
		preloadBettoImages(items.slice(0, preloadCount).map((game) => getImageSrc(game)));
	}, [items, visibleCount]);

	const dragRef = useRef({ startX: null, isDragging: false });

	const getClientX = (e) => {
		if (e.targetTouches && e.targetTouches.length > 0) {
			return e.targetTouches[0].clientX;
		}
		if (e.changedTouches && e.changedTouches.length > 0) {
			return e.changedTouches[0].clientX;
		}
		return e.clientX;
	};

	const handleStart = (e) => {
		const x = getClientX(e);
		dragRef.current.startX = x;
		dragRef.current.isDragging = false;
	};

	const handleMove = (e) => {
		if (dragRef.current.startX === null) return;
		const x = getClientX(e);
		const distance = Math.abs(dragRef.current.startX - x);
		if (distance > 10) {
			dragRef.current.isDragging = true;
		}
	};

	const stepSize = Math.max(1, Math.min(STEP, visibleCount - 2));
	const prev = () => setIndex((current) => Math.max(0, current - stepSize));
	const next = () => setIndex((current) => Math.min(maxIndex, current + stepSize));

	const handleEnd = (e) => {
		if (dragRef.current.startX === null) return;
		const endX = getClientX(e);
		const distance = dragRef.current.startX - endX;
		const minSwipeDistance = 50;

		if (dragRef.current.isDragging && Math.abs(distance) > minSwipeDistance) {
			if (distance > 0) {
				next();
			} else {
				prev();
			}
		}

		dragRef.current.startX = null;
		if (dragRef.current.isDragging) {
			setTimeout(() => {
				dragRef.current.isDragging = false;
			}, 100);
		}
	};

	const handleCancel = () => {
		dragRef.current.startX = null;
		dragRef.current.isDragging = false;
	};

	const handleClickCapture = (e) => {
		if (dragRef.current.isDragging) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const rowAspectRatio = getBettoSliderRowAspectRatio();

	if (!items?.length) return null;

	const hasNav = maxIndex > 0;

	return (
		<WRAPPER>
			<HEADER>
				<TitleBox>
					{icon}
					{title}
				</TitleBox>
				<Controls>
					{hasNav && (
						<>
							<ArrowButton
								type="button"
								onClick={prev}
								disabled={index <= 0}
								aria-label="Previous">
								<ArrowIcon
									src={chevronRight}
									alt=""
									$mirrored
								/>
							</ArrowButton>
							<ArrowButton
								type="button"
								onClick={next}
								disabled={index >= maxIndex}
								aria-label="Next">
								<ArrowIcon
									src={chevronRight}
									alt=""
								/>
							</ArrowButton>
						</>
					)}
					<AllButton
						type="button"
						onClick={() => goToLink(viewAllLink)}>
						{translate("See All")}
					</AllButton>
				</Controls>
			</HEADER>

			<Viewport
				$rowAspectRatio={rowAspectRatio}
				onTouchStart={handleStart}
				onTouchMove={handleMove}
				onTouchEnd={handleEnd}
				onMouseDown={handleStart}
				onMouseMove={handleMove}
				onMouseUp={handleEnd}
				onMouseLeave={handleCancel}
				onClickCapture={handleClickCapture}
				onDragStart={(e) => e.preventDefault()}>
				<Track
					$index={index}
					$count={visibleCount}
					$gap={GAP}>
					{items.map((item, i) => (
						<Card
							$count={visibleCount}
							$gap={GAP}
							$aspectRatio={rowAspectRatio}
							key={`${keyPrefix}-${i}`}>
							{renderItem(item, i)}
						</Card>
					))}
					<ViewAllCard
						$count={visibleCount}
						$gap={GAP}
						$aspectRatio={rowAspectRatio}
						onClick={() => goToLink(viewAllLink)}>
						<ViewAllIcon
							src={chevronRight}
							alt=""
						/>
						<ViewAllLabel>{translate("VIEW_ALL")}</ViewAllLabel>
					</ViewAllCard>
				</Track>
			</Viewport>
		</WRAPPER>
	);
}
